import { Component, Input, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AxiosError } from 'axios';

import { AuthService } from '../services/auth/auth.service';
import { environment } from 'src/environments/environment';
import { errorResponse } from 'src/types/error-response';
import { PayeeHeaderComponent } from '../payee-header/payee-header.component';
import { PayeeFormFieldComponent } from '../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../payee-input/payee-input.directive';
import { MatError } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { MatCheckbox } from '@angular/material/checkbox';
import { PayeeButtonComponent } from '../payee-buttons/payee-button/payee-button.component';
import { FooterSimpleComponent } from '../footer-simple/footer-simple.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-login-template',
  templateUrl: './login-template.component.html',
  styleUrls: ['./login-template.component.scss'],
  imports: [
    PayeeHeaderComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatError,
    RouterLink,
    MatCheckbox,
    PayeeButtonComponent,
    FooterSimpleComponent,
    TranslateModule,
  ],
})
export class LoginTemplateComponent implements OnInit {
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private titleService = inject(Title);
  private snackBar = inject(MatSnackBar);

  @Input() heading: string = 'Belépés';
  @Input() registerPath?: string;
  @Input() forgotPasswordPath?: string;
  @Input() showRememberMe = false;

  readonly loginForm = this.fb.group({
    email: this.fb.control('', {
      validators: [Validators.required, Validators.email],
      nonNullable: true,
    }),
    password: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    remember_me: this.fb.control(false, { nonNullable: true }),
  });

  hidePassword = true;
  readonly loading = signal(false);

  ngOnInit(): void {
    this.titleService.setTitle('Bejelentkezés - Payee');

    if (!this.authService.next) {
      this.authService.setNext(environment.afterLoginPath);
    }
  }

  get email() {
    return this.loginForm.controls.email;
  }
  get password() {
    return this.loginForm.controls.password;
  }
  get remember_me() {
    return this.loginForm.controls.remember_me;
  }

  readonly login = async () => {
    if (this.loginForm.invalid || this.loading()) {
      return;
    }

    const { email, password, remember_me } = this.loginForm.getRawValue();

    try {
      this.loading.set(true);

      await this.authService.login(email, password, remember_me);
      this.pushLoginEvent();
    } catch (e) {
      if (e instanceof AxiosError) {
        const errorData = errorResponse.safeParse(e.response?.data);
        if (errorData.success) {
          this.loginForm.setErrors({
            invalid_password: true,
          });
          return;
        }
      } else {
        console.error('Unknown error while logging in', e);
      }

      this.snackBar.open('Valami hiba történt, próbáld újra késöbb!', 'Ok', {
        duration: 10000,
      });
    } finally {
      this.loading.set(false);
    }
  };

  private pushLoginEvent(): void {
    window.dataLayer?.push({
      event: 'login',
      method: 'email',
    });
  }
}
